import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, Spin } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import { notification } from '../../commonComponents/Notification/Notification';
import {
  fetchSchedulesList,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  type Schedule,
  type SchedulePayload,
} from './store/schedulesSlice';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import ConfirmDialog from '../../commonComponents/ConfirmDialog';
import ScheduleHeader from './ScheduleHeader';
import ScheduleList from './ScheduleList';
import ScheduleFormModal from './ScheduleFormModal';
import type { SchedulesProps } from './types';
import { Container, LoadingContainer } from './styles';
import { localTimeToUTC, utcTimeToLocal } from './utils';

const INITIAL_SCHEDULE_DATA: SchedulePayload = {
  day_names: [],
  start_time: '',
  end_time: '',
};

const Schedules: React.FC<SchedulesProps> = ({ tabIndex }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo(() => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }), [isDarkMode]);
  const antdConfig = useMemo(() => getAntdThemeConfig(isDarkMode), [isDarkMode]);

  const { schedules, loading } = useSelector((state: RootState) => state.schedules);

  const [formOpen, setFormOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [scheduleData, setScheduleData] = useState<SchedulePayload>(INITIAL_SCHEDULE_DATA);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(null);

  const isFetching = loading.fetch;
  const isSubmitting = loading.create || loading.update;
  const isDeleting = loading.delete;

  useEffect(() => {
    if (tabIndex === 8) {
      dispatch(fetchSchedulesList());
    }
  }, [tabIndex, dispatch]);

  const handleCreate = useCallback(() => {
    setIsEdit(false);
    setSelectedScheduleId(null);
    setScheduleData(INITIAL_SCHEDULE_DATA);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((schedule: Schedule) => {
    setIsEdit(true);
    setSelectedScheduleId(schedule.id);
    setScheduleData({
      day_names: schedule.day_names ?? [],
      start_time: schedule.start_time ? utcTimeToLocal(schedule.start_time) : '',
      end_time: schedule.end_time ? utcTimeToLocal(schedule.end_time) : '',
    });
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback((scheduleId: string) => {
    setDeletingScheduleId(scheduleId);
    setDeleteDialogOpen(true);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setScheduleData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDayNamesChange = useCallback((dayNames: string[]) => {
    setScheduleData((prev) => ({ ...prev, day_names: dayNames }));
  }, []);

  const handleFormSubmit = useCallback(async () => {
    if (!scheduleData.start_time?.trim() || !scheduleData.end_time?.trim()) {
      notification.error('Validation', 'Please set both start and end time.');
      return;
    }
    const payload = {
      ...scheduleData,
      start_time: localTimeToUTC(scheduleData.start_time),
      end_time: localTimeToUTC(scheduleData.end_time),
    };
    try {
      if (isEdit && selectedScheduleId) {
        await dispatch(
          updateSchedule({ id: selectedScheduleId, data: payload })
        ).unwrap();
        notification.success('Schedule updated.', 'The schedule has been updated successfully.');
      } else {
        await dispatch(createSchedule(payload)).unwrap();
        notification.success('Schedule created.', 'The schedule has been created successfully.');
      }
      setFormOpen(false);
      dispatch(fetchSchedulesList());
    } catch {
      notification.error('Error saving schedule.', 'An error occurred while saving the schedule.');
    }
  }, [dispatch, isEdit, selectedScheduleId, scheduleData]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingScheduleId) return;
    try {
      await dispatch(deleteSchedule(deletingScheduleId)).unwrap();
      notification.success('Schedule deleted.', 'The schedule has been deleted successfully.');
      setDeleteDialogOpen(false);
      setDeletingScheduleId(null);
      dispatch(fetchSchedulesList());
    } catch {
      notification.error('Error deleting schedule.', 'An error occurred while deleting the schedule.');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingScheduleId(null);
    }
  }, [dispatch, deletingScheduleId]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setSelectedScheduleId(null);
    setScheduleData(INITIAL_SCHEDULE_DATA);
  }, []);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setDeletingScheduleId(null);
    }
  }, [isDeleting]);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={antdConfig}>
        <Container>
          <ScheduleHeader onCreateClick={handleCreate} />
          {isFetching ? (
            <LoadingContainer>
              <Spin size="large" />
            </LoadingContainer>
          ) : (
            <ScheduleList
              schedules={schedules}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )}
          <ScheduleFormModal
            open={formOpen}
            isEdit={isEdit}
            scheduleData={scheduleData}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            onInputChange={handleInputChange}
            onDayNamesChange={handleDayNamesChange}
            submitting={isSubmitting}
          />
          <ConfirmDialog
            isOpen={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            onConfirm={handleDeleteConfirm}
            title="Delete Schedule"
            message="Are you sure? You can't undo this action afterwards."
            confirmText="Delete"
            confirmColorScheme="red"
            loading={isDeleting}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Schedules;
